/* /portaal/dagverslag — mobile-first stream van dagverslagen + daglog
   per kind. Kind-selector bovenaan zodra er meerdere kinderen zijn. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, ArrowLeft, Utensils, Bed, Baby, Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BACKEND_ROOT = API.replace(/\/api$/, "");
const LABEL = { maaltijd: "Maaltijd", "slaap-start": "Slaap start", "slaap-eind": "Slaap eind", luier: "Luier", toilet: "Toilet", activiteit: "Activiteit", notitie: "Notitie" };
const ICON = { maaltijd: Utensils, "slaap-start": Bed, "slaap-eind": Bed, luier: Baby, toilet: Baby, activiteit: Sparkles, notitie: BookOpen };

export default function PortaalDagverslagPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [selKid, setSelKid] = useState(null);
  const [schriftjes, setSchriftjes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const ov = await api.get("/portaal/overview");
        const list = ov.data.kinderen || [];
        setKids(list);
        if (list.length) setSelKid(list[0]);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    })();
  }, [status, user]);

  useEffect(() => {
    if (!selKid) return;
    setLoading(true);
    Promise.all([
      api.get(`/portaal/kind/${selKid.id}/schriftjes`).catch(() => ({ data: [] })),
      api.get(`/portaal/kind/${selKid.id}/photos`).catch(() => ({ data: [] })),
    ]).then(([sr, pr]) => {
      setSchriftjes(sr.data || []);
      setPhotos(pr.data || []);
    }).finally(() => setLoading(false));
  }, [selKid]);

  const timeline = useMemo(() => {
    const items = [];
    (schriftjes || []).forEach(s => items.push({ ...s, _type: "schriftje", _sortKey: s.datum || s.created_at || "" }));
    (photos || []).forEach(p => items.push({ ...p, _type: "photo", _sortKey: (p.created_at || p.uploaded_at || "").slice(0, 19) }));
    return items.sort((a, b) => (b._sortKey || "").localeCompare(a._sortKey || ""));
  }, [schriftjes, photos]);

  if (status === "loading") return null;
  if (status !== "authed" || user?.role !== "ouder") return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="portaal-dagverslag-page">
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="dagverslag-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="text-base font-semibold text-neutral-900">Dagverslag</div>
          <div className="text-xs text-neutral-500">Schriftjes en foto's, nieuwste eerst</div>
        </div>
      </div>

      {kids.length > 1 && (
        <div className="px-4 pt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {kids.map(k => (
            <Button
              key={k.id}
              size="sm"
              variant={selKid?.id === k.id ? "default" : "outline"}
              onClick={() => setSelKid(k)}
              className={selKid?.id === k.id ? "text-white shrink-0" : "shrink-0"}
              style={selKid?.id === k.id ? { background: "var(--sage)" } : {}}
              data-testid={`dagverslag-kid-${k.id}`}
            >{k.voornaam}</Button>
          ))}
        </div>
      )}

      <div className="px-4 pt-4 space-y-3">
        {loading && <div className="text-center text-sm text-neutral-500 py-6">Laden…</div>}
        {!loading && timeline.length === 0 && (
          <Card className="p-6 bg-white text-center" data-testid="dagverslag-empty">
            <BookOpen className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <div className="text-sm text-neutral-600">Nog geen dagverslag voor {selKid?.voornaam}.</div>
            <div className="text-xs text-neutral-400 mt-1">Nieuwe berichten van de leidster komen hier binnen.</div>
          </Card>
        )}

        {timeline.map((item, i) => item._type === "schriftje" ? (
          <Card key={`s-${item.id || i}`} className="p-4 bg-white" data-testid={`dagverslag-schriftje-${item.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-xs text-neutral-500 font-mono">{(item.datum || item.created_at || "").slice(0, 10)}</div>
              <Badge variant="outline" className="text-[10px] uppercase tracking-widest">Schriftje</Badge>
            </div>
            {item.titel && <div className="text-sm font-semibold text-neutral-900 mb-1">{item.titel}</div>}
            {item.hoogtepunt && <div className="text-sm text-neutral-800 mb-2">✨ <em>{item.hoogtepunt}</em></div>}
            {item.tekst && <div className="text-sm text-neutral-700 whitespace-pre-wrap">{item.tekst}</div>}
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-neutral-600">
              {item.stemming && <span className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200">Stemming: {item.stemming}</span>}
              {item.van_naam && <span className="text-neutral-500">— {item.van_naam}</span>}
            </div>
          </Card>
        ) : (
          <Card key={`p-${item.id || i}`} className="overflow-hidden bg-white" data-testid={`dagverslag-photo-${item.id}`}>
            <img src={`${BACKEND_ROOT}${item.url}`} alt="foto" className="w-full h-64 object-cover" />
            {(item.caption || item.author_name) && (
              <div className="p-3 text-xs text-neutral-600">
                {item.caption && <div className="text-sm text-neutral-800 mb-1">{item.caption}</div>}
                <div className="flex items-center gap-1"><Camera className="h-3 w-3" /> {item.author_name || "Team"} · {(item.created_at || "").slice(0, 10)}</div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
